<template>
    <div id="fullscreen-loader" class="cinematic-loader" :class="{ active: data.ready }">
        <div class="top-track">
            <div class="progress-line" :style="{ width: `${data.progress}%` }"></div>
        </div>
        <div class="content">
            <div class="emblem-wrap">
                <div class="aura"></div>
                <div class="emblem">
                    <span class="soundwave-bar"></span>
                    <span class="soundwave-bar"></span>
                    <span class="soundwave-bar"></span>
                    <span class="soundwave-bar"></span>
                    <span class="soundwave-bar"></span>
                </div>
            </div>
            <div class="text-group">
                <h2 class="title">{{ data.name }}</h2>
                <p class="status">{{ data.status }}</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, reactive } from 'vue';
import { useI18n } from 'vue-i18n';

// const user = stores.user();
const modals = stores.modals();
// const apps = stores.apps();

const { t } = useI18n();

const props = defineProps({
    name: {
        type: String,
        default: 'loader'
    }
});

const data = reactive({
    ready: false,
    progress: 0,
    name: '',
    status: ''
});

const close = () => {
    modals.close(props.name);
}

onBeforeUnmount(() => {
    document.documentElement.style.overflow = '';
})

onMounted(() => {
    document.documentElement.style.overflow = 'hidden';

    const payload = modals.getData(props.name);

    data.name = payload?.name;
    data.status = t('pages.main.loader.openingStatus', { name: payload?.name });
    setTimeout(() => {
        data.ready = true;
    })
});
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables.scss' as *;


.cinematic-loader {
    position: fixed;
    inset: 0;
    z-index: 999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background-color: rgba(12, 13, 14, 0.95);
    backdrop-filter: blur(32px);
    -webkit-backdrop-filter: blur(32px);
    @include bapps.transition-smooth(opacity, 0.5s);
    opacity: 0;
    pointer-events: none;

    &.active {
        opacity: 1;
        pointer-events: auto;
    }

    .top-track {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background-color: rgba(255, 255, 255, 0.06);
        z-index: 1000;
    }

    .progress-line {
        height: 100%;
        background: linear-gradient(
            90deg,
            #c084fc 0%,
            #a5b4fc 50%,
            #5eead4 100%
        );
        width: 0%;
        @include bapps.transition-smooth(width, 0.7s, ease-out);
        box-shadow: 0 0 12px rgba(255, 255, 255, 0.5);
    }

    .content {
        text-align: center;
        max-width: 24rem;
        width: 100%;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1.5rem;
    }

    .emblem-wrap {
        position: relative;
        width: 7rem;
        height: 7rem;
        @include bapps.flex-center;
    }

    .aura {
        position: absolute;
        inset: 0;
        border-radius: 1.5rem;
        background: linear-gradient(
            45deg,
            rgba(168, 85, 247, 0.25),
            rgba(99, 102, 241, 0.2),
            rgba(45, 212, 191, 0.25)
        );
        filter: blur(20px);
        animation: ambientGlowPulse 2.8s ease-in-out infinite;
    }

    .emblem {
        position: relative;
        width: 5rem;
        height: 5rem;
        border-radius: 1.5rem;
        background-color: rgba(0, 0, 0, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.1);
        @include bapps.flex-center;
        gap: 0.375rem;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(8px);
    }

    .text-group {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
    }

    .title {
        font-family: $font-display;
        font-weight: 900;
        font-size: 1.875rem;
        color: #ffffff;
        letter-spacing: -0.025em;

        @include bapps.respond-to("sm") {
            font-size: 2.25rem;
        }
    }

    .status {
        font-size: 0.875rem;
        font-weight: 400;
        color: rgba(255, 255, 255, 0.5);
        letter-spacing: normal;
        @include bapps.transition-smooth(opacity, 0.3s);
    }
}

.soundwave-bar {
    width: 0.25rem;
    height: 2rem;
    border-radius: 9999px;
    background-color: rgba(255, 255, 255, 0.9);
    animation: wavePulse 1.4s ease-in-out infinite;
    transform-origin: center;

    &:nth-child(2) {
        animation-delay: 0.18s;
    }
    &:nth-child(3) {
        animation-delay: 0.36s;
    }
    &:nth-child(4) {
        animation-delay: 0.54s;
    }
    &:nth-child(5) {
        animation-delay: 0.72s;
    }
}

@keyframes wavePulse {
    0%,
    100% {
        transform: scaleY(0.3);
        opacity: 0.6;
    }
    50% {
        transform: scaleY(1);
        opacity: 1;
    }
}


@keyframes ambientGlowPulse {
    0%,
    100% {
        transform: scale(0.92);
        opacity: 0.25;
    }
    50% {
        transform: scale(1.15);
        opacity: 0.55;
    }
}
</style>
